import { useModalQueue } from '@carvana/vdp-react-hooks';
import { createContext, useCallback, useMemo, useState } from 'react';
import MoreDetailsModalSections from '~/constants/MoreDetailsModalSections';
import { TileData } from '~/contexts/TileDataContext';
import { AppliedFilters } from '~/contracts';
import { ModalId, ModalPriorities } from './constants';

type MoreDetailsOptions = {
  openTo: MoreDetailsModalSections;
  vehicle: Partial<TileData>;
};

type ContextType = {
  filtersToSave: AppliedFilters;
  isActivationModalOpen: boolean;
  isCoApplicantModalOpen: boolean;
  isCreateSavedSearchModalOpen: boolean;
  isFilterModalOpen: boolean;
  isGeolocationModalOpen: boolean;
  isManageSavedSearchesModalOpen: boolean;
  isMoreDetailsModalOpen: boolean;
  isSoftPullModalOpen: boolean;
  isWipNotificationModalOpen: boolean;
  moreDetailsOptions: MoreDetailsOptions;
  setActivationModalOpen: (isOpen: boolean) => void;
  setCoApplicantModalOpen: (isOpen: boolean) => void;
  setCreateSavedSearchOpen: (isOpen: boolean, filters: AppliedFilters) => void;
  setFilterModalOpen: (isOpen: boolean) => void;
  setGeolocationModalOpen: (isOpen: boolean) => void;
  setManageSavedSearchesOpen: (isOpen: boolean) => void;
  setMoreDetailsModalOpen: (
    isOpen: boolean,
    options?: MoreDetailsOptions
  ) => void;
  setSoftPullModalOpen: (isOpen: boolean) => void;
  setWipNotificationModalOpen: (isOpen: boolean) => void;
};

const defaultContext: ContextType = {
  filtersToSave: {},
  isActivationModalOpen: false,
  isCoApplicantModalOpen: false,
  isCreateSavedSearchModalOpen: false,
  isFilterModalOpen: false,
  isGeolocationModalOpen: false,
  isManageSavedSearchesModalOpen: false,
  isMoreDetailsModalOpen: false,
  isSoftPullModalOpen: false,
  isWipNotificationModalOpen: false,
  moreDetailsOptions: {
    openTo: MoreDetailsModalSections.PriceDetails,
    vehicle: {}
  },
  setActivationModalOpen() {},
  setCoApplicantModalOpen() {},
  setCreateSavedSearchOpen() {},
  setFilterModalOpen() {},
  setGeolocationModalOpen() {},
  setManageSavedSearchesOpen() {},
  setMoreDetailsModalOpen() {},
  setSoftPullModalOpen() {},
  setWipNotificationModalOpen() {}
};

const ModalContext = createContext<ContextType>(defaultContext);

export function ModalContextProvider({ children }) {
  const [currentModalId, generateOpener] = useModalQueue(ModalPriorities);
  const [isFilterModalOpen, setFilterModalOpen] = useState(false);
  const [filtersToSave, setFiltersToSave] = useState<AppliedFilters>({});
  const [moreDetailsOptions, setMoreDetailsOptions] =
    useState<MoreDetailsOptions>(defaultContext.moreDetailsOptions);

  const {
    setActivationModalOpen,
    setCoApplicantModalOpen,
    setCreateSavedSearchOpen,
    setGeolocationModalOpen,
    setManageSavedSearchesOpen,
    setMoreDetailsModalOpen,
    setSoftPullModalOpen,
    setWipNotificationModalOpen
  } = useMemo(
    () => ({
      setActivationModalOpen: generateOpener(ModalId.Activation),
      setCoApplicantModalOpen: generateOpener(ModalId.CoApplicant),
      setCreateSavedSearchOpen: generateOpener(ModalId.CreateSavedSearch),
      setGeolocationModalOpen: generateOpener(ModalId.Geolocation),
      setManageSavedSearchesOpen: generateOpener(ModalId.ManageSavedSearches),
      setMoreDetailsModalOpen: generateOpener(ModalId.MoreDetails),
      setSoftPullModalOpen: generateOpener(ModalId.SoftPull),
      setWipNotificationModalOpen: generateOpener(ModalId.WIPNotifications)
    }),
    [generateOpener]
  );

  const setCreateSavedSearchOpenWithFiltersToSave = useCallback(
    (isOpen: boolean, filters: Partial<AppliedFilters>) => {
      setFiltersToSave(filters);
      setCreateSavedSearchOpen(isOpen);
    },
    [setCreateSavedSearchOpen]
  );

  const setMoreDetailsModalOpenWithVehicle = useCallback<
    ContextType['setMoreDetailsModalOpen']
  >(
    (
      isOpen: boolean,
      options: MoreDetailsOptions = defaultContext.moreDetailsOptions
    ) => {
      setMoreDetailsOptions(options);
      setMoreDetailsModalOpen(isOpen);
    },
    [setMoreDetailsModalOpen]
  );

  const value = useMemo<ContextType>(
    (): ContextType => ({
      filtersToSave: filtersToSave,
      isActivationModalOpen: currentModalId === ModalId.Activation,
      isCoApplicantModalOpen: currentModalId === ModalId.CoApplicant,
      isCreateSavedSearchModalOpen:
        currentModalId === ModalId.CreateSavedSearch,
      isFilterModalOpen,
      isGeolocationModalOpen: currentModalId === ModalId.Geolocation,
      isManageSavedSearchesModalOpen:
        currentModalId === ModalId.ManageSavedSearches,
      isMoreDetailsModalOpen: currentModalId === ModalId.MoreDetails,
      isSoftPullModalOpen: currentModalId === ModalId.SoftPull,
      isWipNotificationModalOpen: currentModalId === ModalId.WIPNotifications,
      moreDetailsOptions,
      setActivationModalOpen,
      setCoApplicantModalOpen,
      setCreateSavedSearchOpen: setCreateSavedSearchOpenWithFiltersToSave,
      setFilterModalOpen,
      setGeolocationModalOpen,
      setManageSavedSearchesOpen,
      setMoreDetailsModalOpen: setMoreDetailsModalOpenWithVehicle,
      setSoftPullModalOpen,
      setWipNotificationModalOpen
    }),
    [
      filtersToSave,
      currentModalId,
      isFilterModalOpen,
      moreDetailsOptions,
      setActivationModalOpen,
      setCoApplicantModalOpen,
      setCreateSavedSearchOpenWithFiltersToSave,
      setGeolocationModalOpen,
      setManageSavedSearchesOpen,
      setMoreDetailsModalOpenWithVehicle,
      setSoftPullModalOpen,
      setWipNotificationModalOpen
    ]
  );

  return (
    <ModalContext.Provider value={value}>{children}</ModalContext.Provider>
  );
}

ModalContextProvider.displayName = 'ModalContextProvider';

export default ModalContext;
